'use client';
import Link from 'next/link';
// Note: SEO meta tags should be handled by generateMetadata in page.tsx (App Router pattern)
// next/head is NOT compatible with Next.js App Router
import Image from 'next/image';
import Breadcrumbs, { type BreadcrumbItem } from './Breadcrumbs';
import FAQSection, { type FAQItem } from './FAQSection';
import { useHeadingIds } from '@/hooks/useHeadingIds';
import ScrollReveal from './ScrollReveal';
import IndustryServicesTabs, { type IndustryServiceTab } from './IndustryServicesTabs';
import type { ReactNode } from 'react';
import type { SchemaNode } from '@/lib/seo/schema';
import { useEffect } from 'react';
import { initScrollTracking, initTimeTracking } from '@/lib/analytics';
import { isCloudflareImageUrl, cfImage, cfImageSrcSet, SIZES } from '@/lib/cloudflare-images';
interface IndustryChallenge {
    icon?: string; // URL to icon image
    title: string;
    description: string;
}
interface ComplianceRequirement {
    framework: string; // e.g., "HIPAA", "PCI-DSS"
    description: string;
}
// Helper function to map framework names to URL slugs
function getComplianceSlug(framework: string): string | null {
    const frameworkMap: Record<string, string> = {
        'HIPAA': 'compliance/hipaa',
        'SOC 2': 'compliance/soc2',
        'SOC2': 'compliance/soc2',
        'NIST': 'compliance/nist',
        'CMMC': 'compliance/cmmc',
        'PCI-DSS': 'compliance/pci-dss',
        'PCI DSS': 'compliance/pci-dss',
        'GDPR': 'compliance/gdpr',
        'HITECH': 'compliance/hipaa#hitech', // HITECH links directly to the HIPAA page section
        'HITECH Act': 'compliance/hipaa#hitech',
    };
    return frameworkMap[framework] || null;
}
interface IndustryCTA {
    title: string;
    description: string;
    primaryButton?: {
        text: string;
        href: string;
    };
    secondaryButton?: {
        text: string;
        href: string;
    };
}
interface RelatedIndustry {
    name: string;
    slug: string;
    description: string;
    icon?: string;
}
interface IndustryLayoutProps {
    industryName: string;
    industrySlug: string;
    industryDescription?: string;
    tagline?: string; // Short tagline for hero section
    // Hero section
    heroImage?: string; // Background image for hero
    heroBackgroundColor?: string; // Default gradient if no image
    // Industry Challenges
    challenges?: IndustryChallenge[];
    // Compliance Requirements
    complianceRequirements?: ComplianceRequirement[];
    // Key Threats
    keyThreats?: string[];
    // Industry service tabs
    serviceTabs?: IndustryServiceTab[];
    // FAQ
    faqItems?: FAQItem[];
    // CTA Section
    ctaSection?: IndustryCTA;
    showCTA?: boolean; // Default: true
    // Related Industries
    relatedIndustries?: RelatedIndustry[];
    showRelatedIndustries?: boolean; // Default: true
    // SEO
    keywords?: string[];
    ogImage?: string;
    // Custom sections
    beforeContent?: ReactNode; // Content before main children
    afterContent?: ReactNode; // Content after main children
    // Main content
    children: ReactNode;
}
export default function IndustryLayout({ industryName, industrySlug, industryDescription, tagline, heroImage, heroBackgroundColor = 'from-[#2cc3d1] to-[#2813e8]', challenges, complianceRequirements, keyThreats, serviceTabs, faqItems, ctaSection, showCTA = true, relatedIndustries, showRelatedIndustries = true, keywords, ogImage, beforeContent, afterContent, children }: IndustryLayoutProps) {
    // Add IDs to all headings for deep linking
    useHeadingIds();

    // Initialize GA4 page-level engagement tracking
    useEffect(() => {
        // Initialize scroll depth tracking (25%, 50%, 75%, 90%)
        const cleanupScroll = initScrollTracking();

        // Initialize time-on-page tracking (30s, 60s, 120s, 300s)
        // Using 'service' as contentType since industries are a type of service page
        const cleanupTime = initTimeTracking('service', industrySlug);

        // Cleanup on unmount
        return () => {
            cleanupScroll();
            cleanupTime();
        };
    }, [industrySlug]);
    // SEO metadata
    const siteUrl = 'https://inventivehq.com';
    const canonicalUrl = `${siteUrl}/industries/${industrySlug}`;
    const pageTitle = `${industryName} Cybersecurity | Inventive HQ`;
    const description = industryDescription || tagline || `Expert cybersecurity solutions for the ${industryName.toLowerCase()} industry. Protect your business with industry-specific security services from Inventive HQ.`;
    const finalOgImage = ogImage || `${siteUrl}/og-images/industry-${industrySlug}.png`;
    // Breadcrumbs
    const breadcrumbs: BreadcrumbItem[] = [
        { label: 'Industries', href: '/industries' },
        { label: industryName }
    ];
    // Default CTA if not provided
    const defaultCTA: IndustryCTA = {
        title: `Ready to Secure Your ${industryName} Business?`,
        description: "Let's discuss how our industry-specific cybersecurity solutions can protect your organization.",
        primaryButton: {
            text: "Get a Free Security Assessment",
            href: "/contact"
        },
        secondaryButton: {
            text: "View All Industries",
            href: "/industries"
        }
    };
    const finalCTA = ctaSection || defaultCTA;
    const sanitizedIndustrySlug = industrySlug.replace(/[^\w-]+/g, '-');
    const contactHrefWithTracking = (href: string, ctaId: string): string => {
        if (!href.startsWith('/contact')) {
            return href;
        }
        try {
            const url = new URL(href, 'https://inventivehq.com');
            if (!url.searchParams.has('ref')) {
                url.searchParams.set('ref', `industry-${sanitizedIndustrySlug}`);
            }
            if (!url.searchParams.has('cta')) {
                url.searchParams.set('cta', ctaId);
            }
            return `${url.pathname}${url.search}`;
        }
        catch (error) {
            console.warn('Failed to apply contact tracking parameters:', error);
            return href;
        }
    };
    // Structured data for Service (Schema.org) - using Service type for industry cybersecurity
    const toAbsoluteUrl = (url?: string) => {
        if (!url)
            return undefined;
        if (url.startsWith('http'))
            return url;
        return `${siteUrl}${url.startsWith('/') ? url : `/${url}`}`;
    };
    const organizationId = `${siteUrl}#organization`;
    const websiteId = `${siteUrl}#website`;
    const webPageId = `${canonicalUrl}#webpage`;
    const serviceId = `${canonicalUrl}#service`;
    // Note: breadcrumbId removed - BreadcrumbList is now rendered by the Breadcrumbs component
    const offerCatalogId = `${canonicalUrl}#offer-catalog`;
    const complianceListId = `${canonicalUrl}#compliance-frameworks`;
    const relatedIndustriesId = `${canonicalUrl}#related-industries`;
    const heroImageUrl = toAbsoluteUrl(heroImage);
    const offerMap = new Map<string, { name: string; description: string; url: string }>();
    (serviceTabs ?? []).forEach(tab => {
        (tab.services ?? []).forEach(service => {
            const absoluteUrl = toAbsoluteUrl(service.href);
            if (!absoluteUrl || offerMap.has(absoluteUrl))
                return;
            offerMap.set(absoluteUrl, {
                name: service.name,
                description: service.description,
                url: absoluteUrl
            });
        });
    });
    const offerItems = Array.from(offerMap.values());
    const offerCatalog = offerItems.length > 0 ? {
        "@type": "OfferCatalog",
        "@id": offerCatalogId,
        "name": `${industryName} Cybersecurity Service Catalog`,
        "itemListElement": offerItems.map((offer, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "Offer",
                "name": offer.name,
                "description": offer.description,
                "url": offer.url,
                "availability": "https://schema.org/InStock",
                "itemOffered": {
                    "@type": "Service",
                    "name": offer.name,
                    "description": offer.description,
                    "serviceType": offer.name,
                    "url": offer.url
                }
            }
        }))
    } : undefined;
    const complianceItemList = complianceRequirements && complianceRequirements.length > 0 ? {
        "@type": "ItemList",
        "@id": complianceListId,
        "name": `${industryName} Compliance Frameworks`,
        "itemListElement": complianceRequirements.map((requirement, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "DefinedTerm",
                "name": requirement.framework,
                "description": requirement.description
            }
        }))
    } : undefined;
    const relatedIndustryList = relatedIndustries && relatedIndustries.length > 0 ? {
        "@type": "ItemList",
        "@id": relatedIndustriesId,
        "name": `Related Industries`,
        "itemListElement": relatedIndustries.map((related, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "WebPage",
                "name": `${related.name} Cybersecurity`,
                "url": `${siteUrl}/industries/${related.slug}`,
                "description": related.description
            }
        }))
    } : undefined;
    const structuredDataGraph: SchemaNode[] = [
        {
            "@type": "Organization",
            "@id": organizationId,
            "name": "Inventive HQ",
            "url": siteUrl,
            "logo": {
                "@type": "ImageObject",
                "url": toAbsoluteUrl('https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/dcb1dccb-de54-47b0-e5a8-a181e5319600/public')
            },
            "sameAs": [
                "https://www.facebook.com/inventivehq/",
                "https://x.com/inventivehq",
                "https://www.instagram.com/inventivehqllc/",
                "https://www.linkedin.com/company/inventivehq/",
                "https://www.youtube.com/@inventivehq"
            ],
            "contactPoint": [
                {
                    "@type": "ContactPoint",
                    "contactType": "sales",
                    "telephone": "+1-866-903-2097",
                    "email": "hello@inventivehq.com",
                    "areaServed": "US"
                }
            ],
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "2305 Historic Decatur Rd Suite 100",
                "addressLocality": "San Diego",
                "addressRegion": "CA",
                "postalCode": "92106",
                "addressCountry": "US"
            }
        },
        {
            "@type": "WebSite",
            "@id": websiteId,
            "url": siteUrl,
            "name": "Inventive HQ",
            "inLanguage": "en-US",
            "publisher": {
                "@id": organizationId
            }
        },
        {
            "@type": "WebPage",
            "@id": webPageId,
            "url": canonicalUrl,
            "name": pageTitle,
            "description": description,
            "isPartOf": {
                "@id": websiteId
            },
            "about": {
                "@id": serviceId
            },
            // Note: BreadcrumbList is rendered by the Breadcrumbs component to avoid duplicates
            "inLanguage": "en-US",
            ...(heroImageUrl && {
                "primaryImageOfPage": {
                    "@type": "ImageObject",
                    "url": heroImageUrl
                }
            })
        },
        {
            "@type": "Service",
            "@id": serviceId,
            "name": `${industryName} Cybersecurity Services`,
            "description": description,
            "serviceType": `${industryName} Cybersecurity`,
            "provider": {
                "@id": organizationId
            },
            "url": canonicalUrl,
            "areaServed": {
                "@type": "Country",
                "name": "United States"
            },
            "audience": {
                "@type": "BusinessAudience",
                "name": `${industryName} Organizations`,
                "industry": industryName
            },
            "category": "Cybersecurity",
            ...(keywords && keywords.length > 0 && { "keywords": keywords }),
            ...(offerCatalog && { "hasOfferCatalog": { "@id": offerCatalogId } }),
            ...(offerItems.length > 0 && {
                "offers": offerItems.map(offer => ({
                    "@type": "Offer",
                    "name": offer.name,
                    "description": offer.description,
                    "url": offer.url
                }))
            }),
            ...(complianceItemList && { "isRelatedTo": { "@id": complianceListId } }),
            ...(relatedIndustryList && { "knowsAbout": relatedIndustries?.map(ind => `${ind.name} Cybersecurity`) })
        }
        // Note: BreadcrumbList is rendered by the Breadcrumbs component to avoid duplicates
    ];
    if (offerCatalog) {
        structuredDataGraph.push(offerCatalog);
    }
    if (complianceItemList) {
        structuredDataGraph.push(complianceItemList);
    }
    if (relatedIndustryList) {
        structuredDataGraph.push(relatedIndustryList);
    }
    if (faqItems && faqItems.length > 0) {
        structuredDataGraph.push({
            "@type": "FAQPage",
            "@id": `${canonicalUrl}#faq`,
            "mainEntity": faqItems.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        });
    }
    const structuredData = {
        "@context": "https://schema.org",
        "@graph": structuredDataGraph
    };
    return (<>
      {/* Note: SEO meta tags are handled by generateMetadata in page.tsx (App Router pattern) */}

      {/* Preload hero image for LCP optimization - React 19 hoists to <head> */}
      {heroImage && isCloudflareImageUrl(heroImage) && (
        <link
          rel="preload"
          as="image"
          href={cfImage(heroImage, { width: 1920, quality: 80 })}
          imageSrcSet={cfImageSrcSet(heroImage, 'fullscreen')}
          imageSizes={SIZES.fullscreen}
        />
      )}

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData)
        }}/>

      <div className="min-h-screen bg-white dark:bg-gray-800">
        {/* Hero Section */}
        <section className={`relative text-white py-20 px-4 overflow-hidden ${!heroImage ? `bg-gradient-to-br ${heroBackgroundColor}` : ''}`}>
          {/* Hero background image - using img for LCP optimization */}
          {heroImage && (<>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={isCloudflareImageUrl(heroImage) ? cfImage(heroImage, { width: 1920, quality: 80 }) : heroImage}
              srcSet={isCloudflareImageUrl(heroImage) ? cfImageSrcSet(heroImage, 'fullscreen') : undefined}
              sizes={isCloudflareImageUrl(heroImage) ? SIZES.fullscreen : undefined}
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              loading="eager"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-[#4B4F58] opacity-50"></div>
          </>)}

          <div className="max-w-7xl mx-auto relative z-10">
            <Breadcrumbs items={breadcrumbs} className="mb-8 text-white opacity-90"/>

            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {industryName} Cybersecurity
              </h1>
              {tagline && (<p className="text-xl md:text-2xl mb-8 opacity-90">
                  {tagline}
                </p>)}
              {industryDescription && (<p className="text-lg md:text-xl opacity-90 max-w-3xl mb-8">
                  {industryDescription}
                </p>)}

              {/* Hero CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                {finalCTA.primaryButton && (<Link href={contactHrefWithTracking(finalCTA.primaryButton.href, 'hero-primary')} className="inline-block bg-white dark:bg-gray-800 text-[#2813e8] hover:bg-gray-100 dark:hover:bg-gray-800 px-8 py-4 rounded-md font-semibold transition-all active:scale-95 shadow-lg text-center">
                    {finalCTA.primaryButton.text}
                  </Link>)}
                {finalCTA.secondaryButton && (<Link href={contactHrefWithTracking(finalCTA.secondaryButton.href, 'hero-secondary')} className="inline-block bg-transparent border-2 border-white text-white hover:bg-white dark:hover:bg-gray-800 hover:text-[#2813e8] px-8 py-4 rounded-md font-semibold transition-all active:scale-95 text-center">
                    {finalCTA.secondaryButton.text}
                  </Link>)}
              </div>
            </div>
          </div>
        </section>

        {/* Before Content Section */}
        {beforeContent && (<div className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              {beforeContent}
            </div>
          </div>)}

        {/* Main Content */}
        <div className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </div>

        {/* Industry Challenges Section */}
        {challenges && challenges.length > 0 && (<section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4">
                  The {industryName} Cybersecurity Challenge
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  Understanding the unique security challenges facing {industryName.toLowerCase()} organizations
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {challenges.map((challenge, index) => (<ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow">
                      {challenge.icon && (<div className="mb-4">
                          <Image src={challenge.icon} alt={challenge.title} width={64} height={64} className="w-16 h-16 object-contain" unoptimized/>
                        </div>)}
                      <h3 className="text-xl font-bold text-[#4B4F58] mb-3">
                        {challenge.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {challenge.description}
                      </p>
                    </div>
                  </ScrollReveal>))}
              </div>
            </div>
          </section>)}

        {/* Compliance Requirements Section */}
        {complianceRequirements && complianceRequirements.length > 0 && (<section className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4" id="compliance-requirements">
                  Compliance Requirements
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  Navigate {industryName.toLowerCase()} regulations with confidence
                </p>
              </div>

              <div className="max-w-4xl mx-auto space-y-6">
                {complianceRequirements.map((requirement, index) => {
                const complianceSlug = getComplianceSlug(requirement.framework);
                const isClickable = complianceSlug !== null;
                const content = (<>
                      <div className="flex-shrink-0 w-12 h-12 bg-[#2813e8] text-white rounded-lg flex items-center justify-center font-bold text-sm">
                        {requirement.framework.substring(0, 3).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-xl font-bold mb-2 ${isClickable ? 'text-[#2813e8] group-hover:underline' : 'text-[#4B4F58]'}`}>
                          {requirement.framework}
                          {isClickable && (<svg className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                            </svg>)}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {requirement.description}
                        </p>
                        {isClickable && (<p className="text-sm text-[#2813e8] mt-3 font-semibold">
                            Learn more about {requirement.framework} compliance →
                          </p>)}
                      </div>
                    </>);
                return (<ScrollReveal key={index} animation="fade-up" delay={index * 60}>
                    {isClickable ? (<Link href={`/services/${complianceSlug}`} className="flex items-start space-x-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:border-[#2813e8] border-2 border-transparent transition-all group cursor-pointer">
                        {content}
                      </Link>) : (<div className="flex items-start space-x-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        {content}
                      </div>)}
                  </ScrollReveal>);
            })}
              </div>
            </div>
          </section>)}

        {/* Key Threats Section */}
        {keyThreats && keyThreats.length > 0 && (<section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4">
                  Key Threats to {industryName} Organizations
                </h2>
              </div>

              <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
                {keyThreats.map((threat, index) => (<ScrollReveal key={index} animation="fade-up" delay={index * 80}>
                    <div className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{threat}</span>
                    </div>
                  </ScrollReveal>))}
              </div>
            </div>
          </section>)}

        {/* Industry Services Tabs */}
        {serviceTabs && serviceTabs.length > 0 && (<section className="py-16 px-4">
            <div className="mx-auto max-w-7xl space-y-10">
              <div className="text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4">
                  Our {industryName} Cybersecurity Services
                </h2>
              </div>
              <IndustryServicesTabs tabs={serviceTabs}/>
            </div>
          </section>)}

        {/* After Content Section */}
        {afterContent && (<div className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              {afterContent}
            </div>
          </div>)}

        {/* CTA Section */}
        {showCTA && (<section className="bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {finalCTA.title}
              </h2>
              <p className="text-lg mb-8 opacity-90">
                {finalCTA.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {finalCTA.primaryButton && (<Link href={contactHrefWithTracking(finalCTA.primaryButton.href, 'cta-primary')} className="inline-block bg-white dark:bg-gray-800 text-[#2813e8] hover:bg-gray-100 dark:hover:bg-gray-800 px-8 py-3 rounded-md font-semibold transition-all active:scale-95">
                    {finalCTA.primaryButton.text}
                  </Link>)}
                {finalCTA.secondaryButton && (<Link href={contactHrefWithTracking(finalCTA.secondaryButton.href, 'cta-secondary')} className="inline-block bg-transparent border-2 border-white text-white hover:bg-white dark:hover:bg-gray-800 hover:text-[#2813e8] px-8 py-3 rounded-md font-semibold transition-all active:scale-95">
                    {finalCTA.secondaryButton.text}
                  </Link>)}
              </div>
            </div>
          </section>)}

        {/* FAQ Section */}
        {faqItems && faqItems.length > 0 && (<div className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              <FAQSection items={faqItems} toolName={`${industryName} Cybersecurity`}/>
            </div>
          </div>)}

        {/* Related Industries */}
        {showRelatedIndustries && relatedIndustries && relatedIndustries.length > 0 && (<section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-2" id="other-industries-we-serve">
                  Other Industries We Serve
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Explore our industry-specific solutions</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedIndustries.map((industry, index) => (<ScrollReveal key={index} animation="fade-up" delay={index * 100}><Link href={`/industries/${industry.slug}`} className="block bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-[#2813e8] hover:shadow-xl transition-all duration-200 group">
                    {industry.icon && (<div className="mb-4">
                        <Image src={industry.icon} alt={industry.name} width={64} height={64} className="w-16 h-16 object-contain" unoptimized/>
                      </div>)}
                    <h3 className="text-xl font-bold mb-3 text-[#4B4F58] group-hover:text-[#2813e8] transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                      {industry.description}
                    </p>
                    <div className="text-[#2813e8] text-sm font-semibold flex items-center">
                      Learn more
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </Link></ScrollReveal>))}
              </div>
            </div>
          </section>)}
      </div>
    </>);
}
// Export types for use in industry pages
export type { IndustryLayoutProps, IndustryChallenge, ComplianceRequirement, IndustryCTA, RelatedIndustry, IndustryServiceTab };
